/* Departments — every department on one sortable sheet, each opening into
 * its shift split, with late arrivals, punch misses and overtime ranked
 * underneath. */

import { useState } from 'react'
import type { ReactNode } from 'react'
import { SK } from '../../lib/api_attendance'
import type { AttDept, AttMonth } from '../../lib/api_attendance'
import { PanelHead } from '../../components/ui'
import { num } from '../../lib/format'
import { missRate, otPerHead, shiftLabel } from './model'
import type { Scope } from './model'
import { BAD, Foot, OK, R, RateRow, Sheet, Sub, Track, heads, listOf, words } from './parts'
import type { People } from './parts'

export type DeptSort =
  'name' | 'avgPresent' | 'active' | 'attPct' | 'latePct' | 'missRate' | 'otHead'

export interface DeptUi { sort: DeptSort; desc: boolean; open: string | null }

interface Props {
  D: AttMonth; S: Scope; target: number; people: People
  ui: DeptUi; setUi: (ui: DeptUi) => void
}

export function DepartmentsTab(props: Props) {
  return (
    <>
      <DeptSheet {...props} />
      <section style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(380px,1fr))', gap: 24,
      }}>
        <Late {...props} />
        <Misses {...props} />
        <Overtime {...props} />
      </section>
    </>
  )
}

// ------------------------------------------------------- the sheet
const COLS = 'minmax(210px,1.35fr) 104px 104px minmax(124px,1fr) 104px 104px 100px'
const SORTS: { k: DeptSort; label: string; right: boolean }[] = [
  { k: 'name', label: 'Department', right: false },
  { k: 'avgPresent', label: 'Present/day', right: true },
  { k: 'active', label: 'Active roll', right: true },
  { k: 'attPct', label: 'Attendance', right: false },
  { k: 'latePct', label: 'Late share', right: true },
  { k: 'missRate', label: 'Punch miss', right: true },
  { k: 'otHead', label: 'OT / head', right: true },
]

function DeptSheet({ D, S, target, people, ui, setUi }: Props) {
  const W = words(people)
  const metric = (d: AttDept): string | number => {
    switch (ui.sort) {
      case 'name': return d.name
      case 'missRate': return missRate(d, S.nDays)
      case 'otHead': return otPerHead(d)
      default: return d[ui.sort]
    }
  }
  const sorted = [...S.depts].sort((a, b) => {
    const x = metric(a), y = metric(b)
    const c = typeof x === 'string' ? x.localeCompare(String(y)) : x - Number(y)
    return ui.desc ? -c : c
  })
  const right = { textAlign: 'right' as const, whiteSpace: 'nowrap' as const, fontSize: 14 }

  return (
    <Sheet>
      <PanelHead title="Department by department" right={
        <div className="no-print" style={{ fontSize: 12, color: 'var(--color-neutral-600)' }}>
          Click a department for its shift split
        </div>
      } />
      <div className="scroll-x">
        <div style={{ minWidth: 1020 }}>
          <div style={{
            display: 'grid', gridTemplateColumns: COLS, gap: 20, padding: '14px 8px 10px',
            borderBottom: '1px solid var(--color-divider)',
          }}>
            {SORTS.map((c) => {
              const on = c.k === ui.sort
              return (
                <button key={c.k} type="button"
                  aria-sort={on ? (ui.desc ? 'descending' : 'ascending') : undefined}
                  // A second click reverses; a new column starts high-to-low,
                  // except the name, which starts at A.
                  onClick={() => setUi({ ...ui, sort: c.k, desc: on ? !ui.desc : c.k !== 'name' })}
                  style={{
                    font: 'inherit', fontSize: 10, letterSpacing: '0.11em',
                    textTransform: 'uppercase', background: 'none', border: 0, padding: 0,
                    cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5,
                    justifyContent: c.right ? 'flex-end' : 'flex-start', whiteSpace: 'nowrap',
                    color: on ? 'var(--color-accent-700)' : 'var(--color-neutral-600)',
                  }}>
                  {c.label}
                  <span style={{
                    fontSize: 12, letterSpacing: 0, fontWeight: on ? 700 : 400,
                    color: on ? 'var(--color-accent)' : 'var(--color-neutral-500)',
                  }}>{on ? (ui.desc ? '↓' : '↑') : '↕'}</span>
                </button>
              )
            })}
          </div>

          {sorted.map((d) => {
            const open = ui.open === d.name
            const tag = d.attPct < 60 ? { t: 'critical', c: 'tag-accent' }
              : d.attPct < target ? { t: 'below target', c: 'tag-outline' }
                : { t: 'on target', c: 'tag-neutral' }
            const mr = missRate(d, S.nDays), oh = otPerHead(d)
            return (
              <div key={d.name}>
                <DeptRow open={open} onClick={() => setUi({ ...ui, open: open ? null : d.name })}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 9, minWidth: 0 }}>
                    <span style={{
                      fontSize: 9, width: 10, flex: 'none',
                      color: open ? 'var(--color-accent)' : 'var(--color-neutral-500)',
                    }}>{open ? '▲' : '▼'}</span>
                    <span style={{
                      fontFamily: 'var(--font-heading)', fontSize: 17, whiteSpace: 'nowrap',
                    }}>{d.name}</span>
                    <span className={`tag ${tag.c}`} style={{
                      fontSize: 10, whiteSpace: 'nowrap', flex: 'none',
                    }}>{tag.t}</span>
                  </div>
                  <div className="num" style={{ textAlign: 'right', fontSize: 15 }}>{num(d.avgPresent, 1)}</div>
                  <div className="num" style={{
                    textAlign: 'right', fontSize: 13, color: 'var(--color-neutral-700)',
                  }}>{num(d.active)}</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Track width={90} height={8} inset={1} stretch={false}
                      value={Math.round((Math.min(d.attPct, 100) / 100) * 90)}
                      fill={d.attPct < target ? BAD : 'var(--c-bar1)'} />
                    <span className="num" style={{ fontSize: 13 }}>{num(d.attPct, 1)}%</span>
                  </div>
                  <div className="num" style={{
                    ...right,
                    color: d.latePct > 38 ? BAD : d.latePct < 30 ? OK : 'var(--color-neutral-800)',
                  }}>{num(d.latePct, 0)}%</div>
                  <div className="num" style={{
                    ...right, color: mr > 6 ? BAD : mr < 2 ? OK : 'var(--color-neutral-800)',
                  }}>{num(mr, 1)}%</div>
                  <div className="num" style={{
                    ...right,
                    color: oh > 20 ? BAD : d.active && oh < 5 ? OK : 'var(--color-neutral-800)',
                  }}>{d.active ? num(oh, 1) + ' h' : '—'}</div>
                </DeptRow>
                {open ? <DeptDetail D={D} d={d} target={target} people={people} /> : null}
              </div>
            )
          })}
        </div>
      </div>
      <Foot>
        Attendance is present days over active-roll days, so a {W.one} who joined or left part-way
        through the period pulls the figure down. Departments are as
        the {people === 'staff' ? 'staff biometric report' : 'workers’ register'} codes them.{' '}
        {people === 'staff'
          ? 'Staff have no register, so punch misses and OT / head are nil by construction.'
          : `Punch miss is misses per 100 present days; OT / head is overtime hours — ${D.otHours.PP} for each PP mark — per worker on the active roll.`}
        {D.reqUnstaffed.length
          ? ` ${listOf(D.reqUnstaffed.map((u) => `${u.name} (${u.req})`))} ${D.reqUnstaffed.length === 1 ? 'is' : 'are'} on the requirement sheet with no ${people} on file.`
          : ''}
      </Foot>
    </Sheet>
  )
}

/** One department's line. The hover wash is state rather than a `:hover`
 *  rule because the design's `.drow` class is not in the stylesheet. */
function DeptRow({ open, onClick, children }: {
  open: boolean; onClick: () => void; children: ReactNode
}) {
  const [over, setOver] = useState(false)
  return (
    <button type="button" onClick={onClick} aria-expanded={open}
      onMouseEnter={() => setOver(true)} onMouseLeave={() => setOver(false)}
      style={{
        font: 'inherit', width: '100%', textAlign: 'left', border: 0,
        background: over ? 'var(--color-accent-100)' : 'none',
        borderBottom: '1px solid var(--color-divider)', cursor: 'pointer',
        color: 'var(--color-text)', display: 'grid', gridTemplateColumns: COLS, gap: 20,
        alignItems: 'center', padding: '12px 8px',
      }}>
      {children}
    </button>
  )
}

function DeptDetail({ D, d, target, people }: {
  D: AttMonth; d: AttDept; target: number; people: People
}) {
  // Each shift carries a tick where the sheet asks for heads on it.
  const want = (k: (typeof SK)[number]) => d.reqShift?.[k] ?? 0
  const scale = Math.max(...SK.map((k) => Math.max(d.byShift[k], want(k)))) * 1.04 || 1
  return (
    <div style={{
      padding: '18px 8px 22px 30px', borderBottom: '1px solid var(--color-divider)',
      background: 'var(--color-accent-100)',
    }}>
      <div className="axis-title">
        {d.name} · {d.req
          ? `average heads present per shift · tick is what the requirement sheet asks for, ${num(d.req)} heads in all`
          : 'average heads present per shift · no sanctioned strength for this department'}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 9, marginTop: 14, maxWidth: 820 }}>
        {SK.map((k) => {
          const ds = D.deptShift[d.name]?.[k]
          return (
            <div key={k} style={{
              display: 'grid', gridTemplateColumns: '130px minmax(120px,1fr) 132px 84px',
              gap: 14, alignItems: 'center',
            }}>
              <div style={{ fontSize: 13, color: 'var(--color-neutral-800)' }}>{shiftLabel(k)}</div>
              <Track width={300} height={14} value={Math.round((d.byShift[k] / scale) * 300)}
                tick={want(k) ? Math.round((want(k) / scale) * 300) : null} />
              <div className="num" style={{ fontSize: 13, textAlign: 'right', whiteSpace: 'nowrap' }}>
                {num(d.byShift[k], 1)}
                {want(k) ? <span style={{ color: 'var(--color-neutral-600)' }}> / {num(want(k))}</span> : null}
              </div>
              <div className="num" style={{
                fontSize: 12, textAlign: 'right', whiteSpace: 'nowrap',
                color: 'var(--color-neutral-600)',
              }}>{ds ? num(ds.late, 0) + '% late' : '—'}</div>
            </div>
          )
        })}
      </div>
      {d.roles.length ? (
        <div className="scroll-x" style={{ marginTop: 18, maxWidth: 820 }}>
        <table className="table" style={{ minWidth: 640 }}>
          <thead>
            <tr>
              <th>Designation</th><th style={R}>Present/day</th><th style={R}>Required</th>
              <th style={R}>Gap</th><th style={R}>On roll</th>
              <th style={R}>A</th><th style={R}>B</th><th style={R}>C</th><th style={R}>Gen</th>
            </tr>
          </thead>
          <tbody>
            {d.roles.map((r) => {
              const g = r.req === null ? null : r.avgPresent - r.req
              const cell = (n: number | undefined) => (n ? num(n) : '–')
              return (
                <tr key={r.designation}>
                  <td>{r.designation}</td>
                  <td className="num" style={R}>{num(r.avgPresent, 1)}</td>
                  <td className="num" style={R}>{r.req === null ? '—' : num(r.req)}</td>
                  <td className="num" style={{ ...R, color: g === null ? 'var(--color-neutral-600)' : g < 0 ? BAD : OK }}>
                    {g === null ? 'not on sheet' : (g > 0 ? '+' : g < 0 ? '−' : '') + num(Math.abs(g), 1)}
                  </td>
                  <td className="num" style={R}>{num(r.active)}</td>
                  <td className="num" style={R}>{cell(r.reqShift?.A)}</td>
                  <td className="num" style={R}>{cell(r.reqShift?.B)}</td>
                  <td className="num" style={R}>{cell(r.reqShift?.C)}</td>
                  <td className="num" style={R}>{cell(r.reqShift?.G)}</td>
                </tr>
              )
            })}
          </tbody>
        </table>
        </div>
      ) : null}
      <Foot top={16}>
        {d.name} carries {heads(d.active, people)} on the active roll and
        puts {num(d.avgPresent, 1)} on the floor on an average day — {num(d.attPct, 1)}% attendance
        {d.req ? `, ${num((d.avgPresent / d.req) * 100, 0)}% of the ${num(d.req)} heads sanctioned` : ''}
        {d.attPct < target ? `, under the ${num(target)}% target` : ''}.{' '}
        {people === 'staff'
          ? `${num(d.late)} late flags across the period.`
          : `${num(d.late)} late flags and ${num(d.miss)} punch misses across the period, ${num(d.flagged)} of them days the biometric flagged for a single or wrong punch and the rest with no punch at all; ${num(d.ot)} overtime hours from twelve-hour marks.`}
        {d.noShift ? ` ${num(d.noShift)} present days have no shift code and sit under 12-hr / other.` : ''}
        {' '}Late share by shift is over the whole period.
      </Foot>
    </div>
  )
}

// ------------------------------------------------------- the three ranks
const inView = (S: Scope) => (d: AttDept) => S.isAll || d.name === S.sel

function Late({ D, S, people }: Props) {
  const rows = S.depts.filter((d) => d.active >= 2).filter(inView(S))
    .sort((a, b) => b.latePct - a.latePct).slice(0, 10)
  const punched = S.presentDays - S.totMiss
  return (
    <Sheet>
      <PanelHead title="Late arrivals" />
      <Sub>
        Share of present days flagged LATE · bars on a full 0–100% scale · {num(S.totLate)} instances
      </Sub>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 9, marginTop: 18 }}>
        {rows.map((d) => (
          <RateRow key={d.name} label={d.name}
            width={Math.round((Math.min(d.latePct, 100) / 100) * 300)}
            fill={d.latePct > 30 ? BAD : 'var(--c-bar1)'}
            value={num(d.latePct, 0) + '%'} aside={num(d.late)} />
        ))}
      </div>
      <Foot wide={false}>
        {S.latePct >= 20
          ? `A LATE flag on ${num(S.latePct, 0)}% of all present days points at the rule, not the ${people}. Worth checking the grace period and the shift-start times held in the biometric master before this is used in any review.`
          : `A LATE flag on ${num(S.latePct, 0)}% of present days. Lateness is a flag in the biometric report, not minutes, so how late is not on file.`}
        {S.totMiss && punched > 0
          ? ` A day with no punch cannot be flagged, so among the ${num(punched)} present days that have one the share is ${num((S.totLate / punched) * 100, 0)}%.`
          : ''}
        {D.dayCount !== S.nDays ? ' Figures are for the days in range.' : ''}
      </Foot>
    </Sheet>
  )
}

function Misses({ D, S, people }: Props) {
  const rows = S.depts.filter(inView(S)).filter((d) => d.miss > 0)
    .sort((a, b) => missRate(b, S.nDays) - missRate(a, S.nDays)).slice(0, 10)
  const max = Math.max(1, ...rows.map((d) => missRate(d, S.nDays)))
  const never = S.isAll ? D.neverPunched : D.deptRegister[S.sel]?.never ?? 0
  // The workers list is whole-period, so this share is too.
  const inDept = D.workers.filter((w) => S.isAll || w.dept === S.sel)
  const allMiss = inDept.reduce((s, w) => s + w.m, 0)
  const neverMiss = inDept.reduce((s, w) => s + (w.never ? w.m : 0), 0)
  const staff = people === 'staff'
  return (
    <Sheet>
      <PanelHead title="Punch misses" />
      <Sub>Misses per 100 present days · {num(S.totMiss)} in the period</Sub>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 9, marginTop: 18 }}>
        {rows.map((d) => {
          const mr = missRate(d, S.nDays)
          return (
            <RateRow key={d.name} label={d.name} width={Math.round((mr / max) * 300)}
              fill={mr > 6 ? BAD : 'var(--c-bar1)'} value={num(mr, 1) + '%'} aside={num(d.miss)} />
          )
        })}
        {rows.length === 0 ? (
          <div style={{ fontSize: 13, color: 'var(--color-neutral-600)' }}>
            {staff ? 'None for staff: there is no register to miss a punch against.' : 'No punch misses in view.'}
          </div>
        ) : null}
      </div>
      <Foot wide={false}>
        {staff ? (
          'A punch miss is a day the register marks someone present and the biometric report has no punch. Staff have no register — their mark is the report’s own word for the day — so there is nothing to miss against and this panel stays empty for them.'
        ) : (
          <>
            A punch miss is a day the register marks a worker present and the biometric report has no
            punch at all. Shown per 100 present days, so a two-man section is comparable with spinning.
            {never
              ? ` ${num(never)} ${never === 1 ? 'worker has' : 'workers have'} no punch on any day of the month — they read as not being on the device, and every present day of theirs is a miss: ${num(neverMiss)} of the ${num(allMiss)} misses over the whole period. The names are on the Workers tab.`
              : ' Anything over 6% is usually one worker whose finger the reader will not take — chase the names on the Workers tab rather than the department.'}
            {' '}Of these, {num(S.totFlagged)} {S.totFlagged === 1 ? 'is a day' : 'are days'} the
            biometric itself flagged for one punch missing or a wrong punch (NO IN PUNCH, NO OUT
            PUNCH, WRONG PUNCH); the rest have no punch at all.
          </>
        )}
      </Foot>
    </Sheet>
  )
}

function Overtime({ D, S, people }: Props) {
  const rows = S.depts.filter((d) => d.ot > 0).filter(inView(S))
    .sort((a, b) => b.ot - a.ot).slice(0, 8)
  const max = Math.max(...rows.map((d) => d.ot), 1)
  const perHead = [...rows].sort((a, b) => otPerHead(b) - otPerHead(a)).slice(0, 2)
  const staff = people === 'staff'
  return (
    <Sheet>
      <PanelHead title="Overtime" />
      <Sub>
        Hours from twelve-hour marks in the register · {D.otHours.PP} per PP
        day, {D.otHours.PPP} per PPP · {num(S.totOt)} hrs total
      </Sub>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 9, marginTop: 18 }}>
        {rows.map((d) => (
          <RateRow key={d.name} label={d.name} width={Math.round((d.ot / max) * 300)}
            fill="var(--c-bar3)" value={num(d.ot) + ' h'} aside={num(otPerHead(d), 0) + '/head'} />
        ))}
        {rows.length === 0 ? (
          <div style={{ fontSize: 13, color: 'var(--color-neutral-600)' }}>
            {staff ? 'None for staff: there are no PP marks to read overtime off.' : 'No twelve-hour marks in view.'}
          </div>
        ) : null}
      </div>
      <Foot wide={false}>
        The reports carry no booked-overtime figure, so this is derived, not read: a PP mark in the
        register is a twelve-hour day and counts {D.otHours.PP} hours over an eight-hour one, a
        PPP {D.otHours.PPP}. Whether those hours were paid as overtime is not on file.
        {staff ? ' Staff have no register and so no PP marks; nothing is read off for them.' : ''}
        {perHead.length
          ? ` ${listOf(perHead.map((d) => d.name))} ${perHead.length === 1 ? 'carries' : 'carry'} the most hours per head.`
          : ''}
      </Foot>
    </Sheet>
  )
}
